(() => {
  'use strict';

  let activeDrawer = null;
  let activeTrigger = null;

  /* =========================================================
     MOVE DRAWER OUTSIDE BODY

     This is intentional.

     Some Shopify themes / theme editor wrappers manipulate
     <body> while scrolling. A fixed drawer inside body can
     therefore become offset or disappear when opened after
     scrolling.

     We attach the drawer directly to <html> instead.
     ========================================================= */

  const portalDrawers = () => {
    document
      .querySelectorAll('.pf-nav')
      .forEach((drawer) => {
        if (drawer.parentElement !== document.documentElement) {
          document.documentElement.appendChild(drawer);
        }
      });
  };


  /* =========================================================
     FOCUSABLE ELEMENTS
     ========================================================= */

  const getFocusableElements = (drawer) => {
    if (!drawer) return [];

    return Array.from(
      drawer.querySelectorAll(
        [
          'a[href]',
          'button:not([disabled])',
          'input:not([disabled])',
          'select:not([disabled])',
          'textarea:not([disabled])',
          '[tabindex]:not([tabindex="-1"])'
        ].join(',')
      )
    ).filter((element) => {
      return (
        !element.hasAttribute('hidden') &&
        element.getAttribute('aria-hidden') !== 'true'
      );
    });
  };


  /* =========================================================
     OPEN DRAWER
     ========================================================= */

  const openDrawer = (drawer, trigger) => {
    if (!drawer) return;

    /*
     * Always move it outside body before opening.
     */
    if (drawer.parentElement !== document.documentElement) {
      document.documentElement.appendChild(drawer);
    }

    /*
     * Close another drawer if needed.
     */
    if (
      activeDrawer &&
      activeDrawer !== drawer
    ) {
      closeDrawer(activeDrawer, false);
    }

    activeDrawer = drawer;
    activeTrigger = trigger || document.activeElement;

    /*
     * IMPORTANT:
     * Do NOT modify:
     *
     * body.position
     * body.top
     * body.height
     * body.overflow
     * html.overflow
     *
     * This keeps the current page scroll position untouched.
     */

    document.documentElement.classList.add(
      'pf-nav-open'
    );

    drawer.classList.add(
      'is-open'
    );

    drawer.setAttribute(
      'aria-hidden',
      'false'
    );

    if (activeTrigger) {
      activeTrigger.setAttribute(
        'aria-expanded',
        'true'
      );
    }

    /*
     * Reset ONLY drawer's internal product scroll.
     */
    const scrollArea =
      drawer.querySelector(
        '.pf-nav__scroll'
      );

    if (scrollArea) {
      scrollArea.scrollTop = 0;
    }

    /*
     * Focus close button without moving viewport.
     */
    requestAnimationFrame(() => {
      const closeButton =
        drawer.querySelector(
          '.pf-nav__close'
        );

      if (!closeButton) return;

      try {
        closeButton.focus({
          preventScroll: true
        });
      } catch (e) {}
    });
  };


  /* =========================================================
     CLOSE DRAWER
     ========================================================= */

  const closeDrawer = (
    drawer = activeDrawer,
    restoreFocus = true
  ) => {
    if (!drawer) return;

    drawer.classList.remove(
      'is-open'
    );

    drawer.setAttribute(
      'aria-hidden',
      'true'
    );

    document.documentElement.classList.remove(
      'pf-nav-open'
    );

    const trigger =
      activeTrigger;

    if (trigger) {
      trigger.setAttribute(
        'aria-expanded',
        'false'
      );
    }

    activeDrawer = null;
    activeTrigger = null;

    if (
      restoreFocus &&
      trigger &&
      typeof trigger.focus === 'function'
    ) {
      requestAnimationFrame(() => {
        try {
          trigger.focus({
            preventScroll: true
          });
        } catch (e) {}
      });
    }
  };


  /* =========================================================
     INITIALIZATION
     ========================================================= */

  const initialize = () => {
    portalDrawers();
  };

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      initialize
    );
  } else {
    initialize();
  }


  /* =========================================================
     OPEN MENU
     ========================================================= */

  document.addEventListener(
    'click',
    (event) => {
      const button =
        event.target.closest(
          '[data-pf-nav-open]'
        );

      if (!button) return;

      event.preventDefault();
      event.stopPropagation();

      portalDrawers();

      const drawerId =
        button.getAttribute(
          'data-pf-nav-open'
        );

      if (!drawerId) return;

      const drawer =
        document.getElementById(
          drawerId
        );

      if (!drawer) {
        console.warn(
          '[Purefemm Drawer] Could not find:',
          drawerId
        );

        return;
      }

      openDrawer(
        drawer,
        button
      );
    }
  );


  /* =========================================================
     CLOSE BUTTON
     ========================================================= */

  document.addEventListener(
    'click',
    (event) => {
      const closeButton =
        event.target.closest(
          '.pf-nav__close'
        );

      if (!closeButton) return;

      const drawer =
        closeButton.closest(
          '.pf-nav'
        );

      if (!drawer) return;

      event.preventDefault();
      event.stopPropagation();

      closeDrawer(drawer);
    }
  );


  /* =========================================================
     OUTSIDE CLICK

     Click anywhere outside .pf-nav__panel = close.
     ========================================================= */

  document.addEventListener(
    'pointerdown',
    (event) => {
      if (!activeDrawer) return;

      const panel =
        event.target.closest(
          '.pf-nav__panel'
        );

      /*
       * Click is inside drawer.
       */
      if (
        panel &&
        activeDrawer.contains(panel)
      ) {
        return;
      }

      /*
       * The clicked element must belong to the
       * open navigation layer.
       */
      if (
        event.target === activeDrawer ||
        activeDrawer.contains(event.target)
      ) {
        event.preventDefault();

        closeDrawer(activeDrawer);
      }
    },
    true
  );


  /* =========================================================
     DIRECT OVERLAY CLICK

     Extra fallback.
     ========================================================= */

  document.addEventListener(
    'click',
    (event) => {
      const overlay =
        event.target.closest(
          '.pf-nav__overlay'
        );

      if (!overlay) return;

      const drawer =
        overlay.closest(
          '.pf-nav'
        );

      if (!drawer) return;

      event.preventDefault();
      event.stopPropagation();

      closeDrawer(drawer);
    },
    true
  );


  /* =========================================================
     ESC / FOCUS TRAP / PREVENT PAGE KEY SCROLL
     ========================================================= */

  document.addEventListener(
    'keydown',
    (event) => {
      if (!activeDrawer) return;

      /*
       * ESC
       */
      if (event.key === 'Escape') {
        event.preventDefault();

        closeDrawer(activeDrawer);

        return;
      }

      /*
       * Prevent keyboard from scrolling page behind drawer.
       */
      const backgroundScrollKeys = [
        'PageDown',
        'PageUp',
        'Home',
        'End'
      ];

      if (
        backgroundScrollKeys.includes(
          event.key
        ) &&
        !event.target.closest(
          '.pf-nav__scroll'
        )
      ) {
        event.preventDefault();
      }

      /*
       * Spacebar background scroll.
       */
      if (
        event.key === ' ' &&
        !event.target.closest(
          'input, textarea, button, a, .pf-nav__scroll'
        )
      ) {
        event.preventDefault();
      }


      /* TAB FOCUS TRAP */

      if (
        event.key !== 'Tab'
      ) {
        return;
      }

      const elements =
        getFocusableElements(
          activeDrawer
        );

      if (!elements.length) return;

      const first =
        elements[0];

      const last =
        elements[
          elements.length - 1
        ];

      if (
        event.shiftKey &&
        document.activeElement === first
      ) {
        event.preventDefault();

        last.focus();

        return;
      }

      if (
        !event.shiftKey &&
        document.activeElement === last
      ) {
        event.preventDefault();

        first.focus();
      }
    }
  );


  /* =========================================================
     WHEEL CONTROL

     We do NOT lock body.

     Instead:
     - wheel inside product area => product area scrolls
     - wheel outside product area => blocked
     ========================================================= */

  document.addEventListener(
    'wheel',
    (event) => {
      if (!activeDrawer) return;

      const scrollArea =
        event.target.closest(
          '.pf-nav__scroll'
        );

      /*
       * Anywhere outside scrollable product area.
       */
      if (!scrollArea) {
        event.preventDefault();

        return;
      }

      const top =
        scrollArea.scrollTop;

      const max =
        scrollArea.scrollHeight -
        scrollArea.clientHeight;

      /*
       * Prevent scroll chaining at top.
       */
      if (
        event.deltaY < 0 &&
        top <= 0
      ) {
        event.preventDefault();

        return;
      }

      /*
       * Prevent scroll chaining at bottom.
       */
      if (
        event.deltaY > 0 &&
        top >= max - 1
      ) {
        event.preventDefault();
      }
    },
    {
      passive: false,
      capture: true
    }
  );


  /* =========================================================
     TOUCH CONTROL
     ========================================================= */

  let touchStartY = 0;

  document.addEventListener(
    'touchstart',
    (event) => {
      if (!activeDrawer) return;

      if (
        event.touches &&
        event.touches[0]
      ) {
        touchStartY =
          event.touches[0].clientY;
      }
    },
    {
      passive: true,
      capture: true
    }
  );


  document.addEventListener(
    'touchmove',
    (event) => {
      if (!activeDrawer) return;

      const scrollArea =
        event.target.closest(
          '.pf-nav__scroll'
        );

      /*
       * Prevent background touch movement.
       */
      if (!scrollArea) {
        event.preventDefault();

        return;
      }

      if (
        !event.touches ||
        !event.touches[0]
      ) {
        return;
      }

      const currentY =
        event.touches[0].clientY;

      const movement =
        currentY - touchStartY;

      const top =
        scrollArea.scrollTop;

      const max =
        scrollArea.scrollHeight -
        scrollArea.clientHeight;

      /*
       * Pull down at top.
       */
      if (
        movement > 0 &&
        top <= 0
      ) {
        event.preventDefault();

        return;
      }

      /*
       * Push up at bottom.
       */
      if (
        movement < 0 &&
        top >= max - 1
      ) {
        event.preventDefault();
      }
    },
    {
      passive: false,
      capture: true
    }
  );


  /* =========================================================
     SHOPIFY THEME EDITOR
     ========================================================= */

  const reinitialize = () => {
    requestAnimationFrame(() => {
      portalDrawers();
    });
  };

  document.addEventListener(
    'shopify:section:load',
    reinitialize
  );

  document.addEventListener(
    'shopify:section:reorder',
    reinitialize
  );

  document.addEventListener(
    'shopify:section:select',
    reinitialize
  );

  document.addEventListener(
    'shopify:block:select',
    reinitialize
  );


  /* =========================================================
     PAGE CLEANUP
     ========================================================= */

  window.addEventListener(
    'pagehide',
    () => {
      document.documentElement.classList.remove(
        'pf-nav-open'
      );

      if (activeDrawer) {
        activeDrawer.classList.remove(
          'is-open'
        );
      }

      activeDrawer = null;
      activeTrigger = null;
    }
  );

})();